import { useCallback, useEffect } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';

import * as releaseApi from 'api/releaseApi';
import { REGISTRATION_STEP } from 'constants/registrationStep';
import { useToast } from 'hooks/useToast';
import { useAppDispatch, useAppSelector } from 'store/hooks/useStore';
import { loaderActions } from 'store/reducers/loaderReducer/loaderSlice';
import { listSelectors } from 'store/reducers/releaseReducers/releaseComponentReducer/listSlice';
import {
  editActions,
  editSelectors,
} from 'store/reducers/releaseReducers/releaseEnvironmentReducer/editSlice';
import { registrationActions } from 'store/reducers/releaseReducers/releaseEnvironmentReducer/registrationSlice';
import { ReleaseEnvironment } from 'types/releaseEnvironment';
import { EditHookReturnType } from '../types';
import { useListStore } from './useListStore';

/**
 * 環境登録 store の実装を環境から隠すための中間層
 */
export const useEditStore = (): EditHookReturnType => {
  const componentList = useAppSelector(listSelectors.releaseComponents).map(
    (value) => ({ id: value.id, name: value.componentName }),
  );
  const dispatch = useAppDispatch();
  const { fetchReleaseEnvironments } = useListStore();
  const toast = useToast();
  const form = useForm<ReleaseEnvironment>({
    mode: 'all',
  });

  const { getValues, reset, handleSubmit, control, watch } = form;

  const { fields, append, remove } = useFieldArray({
    name: 'components',
    control,
  });
  const watchFieldArray = watch('components');
  const controlledFields = fields.map((field, index) => ({
    ...field,
    ...watchFieldArray[index],
  }));

  const onAppend = () => {
    append({ id: undefined, version: '' });
  };

  const onRemove = (index: number) => {
    remove(index);
  };

  const currentModalStep = useAppSelector(editSelectors.currentModalStep);

  const onCloseModal = () => {
    dispatch(registrationActions.initialize());
    dispatch(editActions.initialize());
    reset();
  };

  const onCloseModalWhenCompleted = () => {
    onCloseModal();
    void fetchReleaseEnvironments();
  };

  const onConfirm = () => {
    dispatch(registrationActions.updateStep(REGISTRATION_STEP.CONFIRMATION));
  };

  const onGoBack = () => {
    dispatch(registrationActions.updateStep(REGISTRATION_STEP.INPUT));
  };

  const onSubmit = handleSubmit(
    async (values: ReleaseEnvironment) => {
      dispatch(loaderActions.setLoading(true));
      const EnvironmentId = values.manifestVersion || '';
      const result = await releaseApi.editEnvironment(EnvironmentId, values);
      if (result.ok) {
        dispatch(registrationActions.updateStep(REGISTRATION_STEP.RESULT));
      } else {
        toast({
          status: 'error',
          description: `環境の登録に失敗しました: ${result.err.message}`,
        });
      }
      dispatch(loaderActions.setLoading(false));
    },
    () => {
      toast({
        status: 'error',
        description: '環境の登録に失敗しました。',
      });
    },
  );

  const onOpenModalDelete = useCallback(() => {
    dispatch(editActions.updateIsOpenModalDelete(true));
  }, [dispatch]);

  const selectedReleaseEnvironment = useAppSelector(
    editSelectors.selectedReleaseEnvironment,
  );

  useEffect(() => {
    if (selectedReleaseEnvironment) {
      reset(selectedReleaseEnvironment);
    }
  }, [selectedReleaseEnvironment, reset]);

  return {
    mode: '更新',
    isOpenModal: !!selectedReleaseEnvironment,
    currentModalStep,
    onOpenModal: () => null,
    onCloseModal,
    onCloseModalWhenCompleted,
    onConfirm,
    onGoBack,
    onSubmit,
    onOpenModalDelete,
    form,
    values: getValues(),
    onAppend,
    onRemove,
    controlledFields,
    componentList,
  };
};
